import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { requestMarkHudTourSeen } from "../../services/userService.js";
import { getFreshIdToken } from "../../utils/authToken.js";
import { LiveStatus, refocusIfLost, useOnChange } from "./workspaceA11y.js";
import "./HudWalkthrough.css";

/**
 * HudWalkthrough — a lightweight, non-blocking spotlight tour for the lesson
 * HUDs. The first time a user lands in a given HUD type it auto-runs a few
 * coach-mark steps, each highlighting one pane. It is shown once per HUD type,
 * remembered per account (Firestore, via requestMarkHudTourSeen) with a
 * localStorage flag as the immediate / anonymous fallback.
 *
 * The card is a modal dialog for keyboard and screen-reader users: focus moves
 * to its main button when it opens, Tab stays inside it, and focus goes back to
 * whatever had it before when it closes. The page still takes mouse clicks.
 *
 * Props:
 *   hud             — HUD key ("canvas" | "promptLab" | "editor")
 *   steps           — array of { selector, title, body, placement } for this hud
 *   forceOpenSignal — increment from the parent to replay (ignores the seen flag)
 *   user            — UserContext user (for per-account persistence)
 */

const storageKey = (hud) => `codenec.hudTour.${hud}`;

const hasSeen = (hud, user) => {
    if (!hud) return true;
    try {
        if (localStorage.getItem(storageKey(hud))) return true;
    } catch (e) {
        // localStorage unavailable (private mode etc.) — fall through
    }
    return Array.isArray(user?.seenHudTours) && user.seenHudTours.includes(hud);
};

// Place the coach-mark card on the preferred side of the target rect, flipping /
// clamping so it always stays inside the viewport.
const computeCardPos = (rect, placement, cardEl) => {
    const gap = 14;
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cw = cardEl?.offsetWidth || 330;
    const ch = cardEl?.offsetHeight || 170;

    let top;
    let left;
    switch (placement) {
        case "left":
            left = rect.left - cw - gap;
            top = rect.top;
            break;
        case "top":
            top = rect.top - ch - gap;
            left = rect.left;
            break;
        case "bottom":
            top = rect.bottom + gap;
            left = rect.left;
            break;
        case "center":
            top = (vh - ch) / 2;
            left = (vw - cw) / 2;
            break;
        case "right":
        default:
            left = rect.right + gap;
            top = rect.top;
            break;
    }

    // If the preferred horizontal side overflows, try the opposite side.
    if (left + cw > vw - margin) left = rect.left - cw - gap;
    if (left < margin) left = rect.right + gap;
    // Final clamp.
    left = Math.max(margin, Math.min(left, vw - cw - margin));
    top = Math.max(margin, Math.min(top, vh - ch - margin));

    return { top, left };
};

// Where focus goes on close when nothing had it before, as when the tour opened
// by itself: the pill that replays it, else the page's <main>.
const fallbackFocusTarget = () =>
    document.querySelector(".hud-wt-replay-btn:not(.hud-hint-btn)") || document.querySelector("main");

const HudWalkthrough = ({ hud, steps, forceOpenSignal = 0, user }) => {
    const { t } = useTranslation();
    const [active, setActive] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const [cardPos, setCardPos] = useState(null);
    const [stepNote, setStepNote] = useState("");
    const cardRef = useRef(null);
    const primaryRef = useRef(null);
    const openerRef = useRef(null);
    const focusedRef = useRef(false);
    const labelId = useId();
    const titleId = useId();
    const bodyId = useId();

    const validSteps = useMemo(() => (Array.isArray(steps) ? steps : []), [steps]);
    const total = validSteps.length;
    const step = validSteps[stepIndex] || null;

    // First index >= `from` whose target is currently in the DOM (-1 if none).
    const findRenderableStep = useCallback(
        (from) => {
            for (let i = from; i < validSteps.length; i++) {
                if (document.querySelector(validSteps[i].selector)) return i;
            }
            return -1;
        },
        [validSteps]
    );

    // Mark the tour as seen (both fallbacks) — used by Skip and Done.
    const markSeen = useCallback(() => {
        if (!hud) return;
        try {
            localStorage.setItem(storageKey(hud), "1");
        } catch (e) {
            // ignore
        }
        if (user && !user.isAnonymous && user.accessToken) {
            getFreshIdToken(user.accessToken).then((token) => {
                if (token) requestMarkHudTourSeen(hud, token);
            });
        }
    }, [hud, user]);

    const close = useCallback(() => {
        markSeen();
        setActive(false);
    }, [markSeen]);

    // Measure the current step's target; advance past targets that disappeared.
    const measure = useCallback(() => {
        if (!step) return;
        const el = document.querySelector(step.selector);
        if (!el) {
            const nxt = findRenderableStep(stepIndex + 1);
            if (nxt >= 0) setStepIndex(nxt);
            else close();
            return;
        }
        setRect(el.getBoundingClientRect());
    }, [step, stepIndex, findRenderableStep, close]);

    // Auto-open the first time this HUD is entered (after panes have painted).
    useEffect(() => {
        if (total === 0 || hasSeen(hud, user)) return undefined;
        let raf1;
        let raf2;
        const t = setTimeout(() => {
            raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    const first = findRenderableStep(0);
                    if (first >= 0) {
                        setStepIndex(first);
                        setActive(true);
                    }
                });
            });
        }, 350);
        return () => {
            clearTimeout(t);
            if (raf1) cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hud]);

    // Replay when the parent bumps the signal (ignores the seen flag).
    useEffect(() => {
        if (!forceOpenSignal) return;
        const first = findRenderableStep(0);
        if (first >= 0) {
            setStepIndex(first);
            setActive(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceOpenSignal]);

    // Re-measure on open, step change, and on resize/scroll.
    useLayoutEffect(() => {
        if (!active) return undefined;
        const raf = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(raf);
    }, [active, stepIndex, measure]);

    useEffect(() => {
        if (!active) return undefined;
        const onChange = () => measure();
        window.addEventListener("resize", onChange);
        window.addEventListener("scroll", onChange, true);
        return () => {
            window.removeEventListener("resize", onChange);
            window.removeEventListener("scroll", onChange, true);
        };
    }, [active, measure]);

    // Position the card once we have a rect (and the card is in the DOM).
    useLayoutEffect(() => {
        if (!active || !rect) return;
        setCardPos(computeCardPos(rect, step?.placement, cardRef.current));
    }, [active, rect, step]);

    const next = useCallback(() => {
        const nxt = findRenderableStep(stepIndex + 1);
        if (nxt >= 0) setStepIndex(nxt);
        else close();
    }, [findRenderableStep, stepIndex, close]);

    const back = useCallback(() => {
        for (let i = stepIndex - 1; i >= 0; i--) {
            if (document.querySelector(validSteps[i].selector)) {
                setStepIndex(i);
                return;
            }
        }
    }, [stepIndex, validSteps]);

    // Keyboard navigation while the tour is open. Escape works anywhere; the
    // arrows, Enter and Tab only from inside the card, so keys typed in the
    // lesson are never taken. Buttons keep their own Enter.
    useEffect(() => {
        if (!active) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                close();
                return;
            }
            const card = cardRef.current;
            if (!card || !card.contains(e.target)) return;
            if (e.key === "ArrowRight" || (e.key === "Enter" && e.target.tagName !== "BUTTON")) {
                e.preventDefault();
                next();
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                back();
            } else if (e.key === "Tab") {
                const buttons = card.querySelectorAll("button");
                const first = buttons[0];
                const last = buttons[buttons.length - 1];
                if (e.shiftKey && e.target === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && e.target === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, close, next, back]);

    // Set while the whole component unmounts (leaving the lesson), when the
    // fallback below must not pull focus into the next page. Declared first, so
    // its cleanup runs before the one below.
    const unmountingRef = useRef(false);
    useEffect(() => {
        unmountingRef.current = false;
        return () => {
            unmountingRef.current = true;
        };
    }, []);

    // Remember what had focus when the tour opened and give it back on close,
    // unless the student has since put focus somewhere else.
    useEffect(() => {
        if (!active) return undefined;
        openerRef.current = document.activeElement;
        return () => {
            const opener = openerRef.current;
            openerRef.current = null;
            focusedRef.current = false;
            setStepNote("");
            const now = document.activeElement;
            if (now && now !== document.body) return;
            if (opener && opener !== document.body && opener.isConnected) opener.focus();
            else if (!unmountingRef.current) fallbackFocusTarget()?.focus();
        };
    }, [active]);

    // Focus the main button once the card is placed: it stays hidden until then,
    // and a hidden element can't take focus.
    useEffect(() => {
        if (!active || !cardPos || focusedRef.current) return;
        focusedRef.current = true;
        primaryRef.current?.focus();
    }, [active, cardPos]);

    // A step change can remove the focused button (Back, on the first step).
    useEffect(() => {
        if (active && focusedRef.current) refocusIfLost(primaryRef.current);
    }, [active, stepIndex]);

    // Focus stays on the same button from step to step, so nothing would be
    // read; the card's status line reads the new step.
    useOnChange(stepIndex, (i) => {
        const s = validSteps[i];
        if (!active || !focusedRef.current || !s) return;
        setStepNote(`${t("hudWt.stepCount", { current: i + 1, total })}. ${s.title}. ${s.body}`);
    });

    if (!active || !step) return null;

    const isLast = findRenderableStep(stepIndex + 1) === -1;
    let hasPrev = false;
    for (let i = stepIndex - 1; i >= 0; i--) {
        if (document.querySelector(validSteps[i].selector)) {
            hasPrev = true;
            break;
        }
    }

    const PAD = 6;
    const spotlightStyle = rect
        ? {
              top: rect.top - PAD,
              left: rect.left - PAD,
              width: rect.width + PAD * 2,
              height: rect.height + PAD * 2,
          }
        : null;

    return createPortal(
        <div className="hud-wt">
            {spotlightStyle && <div className="hud-wt-spotlight" style={spotlightStyle} />}
            <div
                className="hud-wt-card"
                ref={cardRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${labelId} ${titleId}`}
                aria-describedby={bodyId}
                style={cardPos ? { top: cardPos.top, left: cardPos.left } : { top: 24, left: 24, visibility: "hidden" }}
            >
                <span id={labelId} className="sr-only">{t("promptLab.walkthrough")}</span>
                <LiveStatus message={stepNote} />
                <div className="hud-wt-card-header">
                    <span className="hud-wt-step-count">
                        {t("hudWt.stepCount", { current: stepIndex + 1, total })}
                    </span>
                    <button type="button" className="hud-wt-skip" onClick={close}>
                        {t("hudWt.skip")}
                    </button>
                </div>
                <h5 id={titleId} className="hud-wt-title">{step.title}</h5>
                <p id={bodyId} className="hud-wt-body">{step.body}</p>
                <div className="hud-wt-footer">
                    <div className="hud-wt-dots" aria-hidden="true">
                        {validSteps.map((s, i) => (
                            <span key={i} className={`hud-wt-dot ${i === stepIndex ? "is-active" : ""}`} />
                        ))}
                    </div>
                    <div className="hud-wt-actions">
                        {hasPrev && (
                            <button type="button" className="hud-wt-btn hud-wt-btn--ghost" onClick={back}>
                                {t("regular.back")}
                            </button>
                        )}
                        <button type="button" ref={primaryRef} className="hud-wt-btn hud-wt-btn--primary" onClick={next}>
                            {isLast ? t("hudWt.done") : t("regular.next")}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default HudWalkthrough;
